import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { ColDef, IServerSideGetRowsRequest } from 'ag-grid-community';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import {
  buildAuditTrailColumnGroup,
  buildOwnerColumnDef,
} from 'src/app/ag-grid/utils/audit-trail-columns.utils';
import { buildNomenclatureColumnDef } from 'src/app/ag-grid/utils/nomenclature-column.utils';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { DataObject } from 'src/app/core/models/core.models';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { ToastService } from '@services/toast.service';
import { redirectToObject } from 'src/app/shared/utils/object-utils';
import { DataGridHostBase } from 'src/app/ag-grid/components/data-grid/data-grid-host-base';

@Component({
  selector: 'app-query-used-by',
  templateUrl: './query-used-by.component.html',
  styleUrls: ['./query-used-by.component.scss'],
  standalone: false,
})
export class QueryUsedByComponent extends DataGridHostBase {
  /** The query whose usages the grid lists; drives every fetch. */
  @Input() object: DataObject;
  /** Feeds the used-by tab counter in the query editor dialog. */
  @Output() rowCountChanged = new EventEmitter<number>();

  private readonly utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private readonly nomenclatureService = inject(NomenclatureService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  readonly viewId = ViewId.QueryUsedBy;
  readonly title = this.translateService.instant('grid.title.feeds');
  readonly icon: IconProp = ['fas', 'rss'];

  /**
   * No toolbar paginator ([paginated]="false" in the template): the `used_by`
   * endpoint clamps `rowCount` to the length of the block it just returned
   * (UtilsDataObjectPOST, "Fix row counts based on returned data length"), so
   * the response never carries a real total to page over. Needs a backend fix
   * before this view can page.
   */
  readonly fetchRows = (request: IServerSideGetRowsRequest) =>
    this.utilsDataObjectApi
      .fetchUsedByObjectsAgGrid(
        this.object.id,
        this.object.object_class_id,
        request,
      )
      .pipe(
        tap((response: any) => {
          if (response && typeof response.rowCount === 'number') {
            this.rowCountChanged.emit(response.rowCount);
          }
        }),
      );

  readonly columnDefs: ColDef[] = this.buildColumnDefs();

  private buildColumnDefs(): ColDef[] {
    return [
      {
        headerName: this.translateService.instant('grid.column.name'),
        field: 'title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'title',
          onClick: (data) => {
            redirectToObject(data, this.router, true);
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        ...buildNomenclatureColumnDef(
          this.translateService,
          this.nomenclatureService,
          {
            headerNameKey: 'grid.column.type',
            fieldKey: 'object_type.id',
            fieldValue: 'object_type.title',
            nomenclatureName: 'query_type',
            otherFilterParams: {
              defaultToNothingSelected: true,
            },
          },
        ),
      },
      {
        headerName: this.translateService.instant('grid.column.description'),
        field: 'description',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: this.translateService.instant(
          'grid.column.dimensions-count',
        ),
        field: 'dimensions_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
      },
      {
        headerName: this.translateService.instant('grid.column.alerts-count'),
        field: 'alerts_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedules-count',
        ),
        field: 'export_schedules_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.algorithms-count',
        ),
        field: 'algorithms_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
      },
      {
        headerName: this.translateService.instant('grid.column.workspace'),
        field: 'workspace.title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'title',
          icon: ['fas', 'folder'],
          object: 'workspace',
          onClick: (workspace, row) => {
            if (row.can_view && row.can_edit) {
              redirectToObject(workspace, this.router, true);
            } else {
              this.toastService.showInfo(
                this.translateService.instant(
                  'feeds.error.you-do-not-have-permissions-or-object-not-active',
                ),
                this.translateService.instant('feeds.error.not-available'),
              );
            }
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      buildOwnerColumnDef(this.translateService, this.openUser),
      buildAuditTrailColumnGroup(this.translateService, {
        area: 'analytics',
        onUserClick: this.openUser,
      }),
    ];
  }
}
